/**
 * B614 — the SHOW face's poster (Figma 02 · Screens 553:2 @1440, 555:130 @390;
 * redline-storefront-show-poster-wall.json). A comedy club's listing is a
 * wall of posters, not tiles: the photo full-bleed with the HEADLINER over its
 * foot (the lineup line under it, B617), the age badge and one urgency flag at
 * its top, then the nights it runs — a showtime chip per slot that goes
 * straight to that showtime's tickets — and the price foot.
 *
 * Every fact is the caller's (a read carried it); the card invents none. A
 * sold-out showtime is still drawn (dimmed, not pressable) because a buyer who
 * sees Friday 9:45 gone learns to pick 7:00, and a missing chip teaches nothing.
 * Cards HUG their content (NOTE 555:249) — the wall never equalises heights.
 */
import type { ReactNode } from "react";
import { sizedPhotoUrl } from "./media.js";

export interface PosterShowtime {
  readonly id: string;
  /** "7:00" — the venue's wall clock. */
  readonly time: string;
  /** open, few left (still sold), or sold out (drawn dim, not pressable). */
  readonly state: "open" | "few" | "soldout";
  /** The chip's full name for assistive tech ("Fri 2 Oct, 7:00"). */
  readonly label: string;
}

export interface PosterNight {
  /** "FRI 2" — the night's short name, as the frame draws it. */
  readonly day: string;
  readonly showtimes: readonly PosterShowtime[];
}

export function PosterCard(props: {
  readonly headliner: string;
  /** B617 — the rest of the bill ("with Kel Mitchell · hosted by Ali Siddiq"). */
  readonly lineup?: string;
  readonly image?: { readonly url: string; readonly alt?: string };
  /** "21+" or "21+ · Sun 18+" — B615's age, in the frame's amber. */
  readonly age?: string;
  /** One urgency flag: "FEW LEFT · SAT 9:45" (green) or "SOLD OUT · FRI 9:45" (red). */
  readonly flag?: { readonly tone: "few" | "soldout"; readonly label: string };
  readonly nights: readonly PosterNight[];
  /** Said where the nights would be when none fall in the window. */
  readonly noNights?: string;
  /** "from $35" — the face's figure. */
  readonly price?: string;
  /** "GA $35 · VIP front section $55" — the ticket types, small. */
  readonly priceDetail?: string;
  readonly onShowtime: (id: string) => void;
  /** The poster's own door (the tickets step with no night chosen). */
  readonly onOpen: () => void;
  /** The face's accessible name — the caller's words (the package ships no industry noun). */
  readonly openLabel: string;
  readonly redline?: string;
}): ReactNode {
  return (
    <article className="t-poster" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <button type="button" className="t-poster-face" onClick={props.onOpen} aria-label={props.openLabel}>
        {props.image !== undefined ? (
          <img className="t-poster-photo" src={sizedPhotoUrl(props.image.url, 640)} alt={props.image.alt ?? ""} loading="lazy" />
        ) : null}
        <span className="t-poster-scrim" aria-hidden="true" />
        {props.age !== undefined || props.flag !== undefined ? (
          <span className="t-poster-badges">
            {props.age !== undefined ? <span className="t-poster-age">{props.age}</span> : null}
            {props.flag !== undefined ? <span className={`t-poster-flag t-poster-flag--${props.flag.tone}`}>{props.flag.label}</span> : null}
          </span>
        ) : null}
        <span className="t-poster-bill">
          <span className="t-poster-headliner">{props.headliner}</span>
          {props.lineup !== undefined ? <span className="t-poster-lineup">{props.lineup}</span> : null}
        </span>
      </button>
      <div className="t-poster-body">
        {props.nights.length > 0 ? (
          <ul className="t-poster-nights">
            {props.nights.map((night) => (
              <li key={night.day} className="t-poster-night">
                <span className="t-poster-day">{night.day}</span>
                <span className="t-poster-times">
                  {night.showtimes.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`t-showchip t-showchip--${s.state}`}
                      disabled={s.state === "soldout"}
                      aria-label={s.state === "soldout" ? `${s.label} — sold out` : s.state === "few" ? `${s.label} — few left` : s.label}
                      onClick={() => props.onShowtime(s.id)}
                    >
                      {s.time}
                    </button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ) : props.noNights !== undefined ? (
          <p className="t-poster-none">{props.noNights}</p>
        ) : null}
        {props.price !== undefined || props.priceDetail !== undefined ? (
          <p className="t-poster-foot">
            {props.price !== undefined ? <span className="t-poster-price">{props.price}</span> : null}
            {props.priceDetail !== undefined ? <span className="t-poster-types">{props.priceDetail}</span> : null}
          </p>
        ) : null}
      </div>
    </article>
  );
}
